/**
 * 视口换算。
 *
 * 关键：这四个变量都是 **视口的 1%**，与 CSS 的 1vmin / 1vmax / 1vw / 1vh 同义。
 * 例如 1800x903 的视口下 --vmin = 9.03px，于是圆的 calc(var(--vmin) * 110) = 993px。
 *
 * （抓取线上页面时实测根元素上就是 `--vmin: 9.58px; --vmax: 18.00px; --vw: 18.00px;
 *   --vh: 9.03px` —— 单位是百分比，不是像素长度。）
 *
 * 全站尺寸都以这些变量的倍数表达；CSS 里给出了 1vmin/1vw 的回退值，
 * 这个 hook 把原站行为补齐，保证与线上取到同一个数字。
 */
import { useEffect, useState } from 'react';

export interface Viewport {
  width: number;
  height: number;
  /** min(width, height) */
  vmin: number;
  vmax: number;
  vw: number;
  vh: number;
}

function measure(): Viewport {
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    width,
    height,
    // 与 CSS 的 1vmin/1vmax/1vw/1vh 同义：都是视口的 1%
    vmin: Math.min(width, height) / 100,
    vmax: Math.max(width, height) / 100,
    vw: width / 100,
    vh: height / 100,
  };
}

export function useViewportVars(): Viewport {
  const [vp, setVp] = useState<Viewport>(() =>
    typeof window === 'undefined'
      ? { width: 0, height: 0, vmin: 0, vmax: 0, vw: 0, vh: 0 }
      : measure(),
  );

  useEffect(() => {
    let frame = 0;
    const apply = () => {
      const next = measure();
      setVp(next);
      const root = document.documentElement;
      root.style.setProperty('--vmin', `${next.vmin}px`);
      root.style.setProperty('--vmax', `${next.vmax}px`);
      root.style.setProperty('--vw', `${next.vw}px`);
      root.style.setProperty('--vh', `${next.vh}px`);
    };
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  return vp;
}
